<template>
  <v-dialog
    v-model="$parent.fundPerformanceDialog"
    :fullscreen="$vuetify.display.xs"
    persistent
    width="600px"
  >
    <v-card>
      <!-- Header -->
      <v-row no-gutters align="center" class="pl-6 py-2 primary">
        <v-col>
          <h4 class="text-h6">Dynamic Fund Selection</h4>
          <v-btn
            variant="text"
            class="position-absolute top-0 right-0 rounded-lg pa-3 d-inline-block"
            :icon="mdiClose"
            @click="$parent.fundPerformanceDialog = false"
          />
        </v-col>
      </v-row>

      <!-- Body -->
      <v-card-text>
        <v-container fluid pa-0>
          <v-row>
            <!-- Note -->
            <v-col cols="12" class="text-body-1">
              <v-alert :value="true" type="info" class="d-inline-flex">
                Filers are selected each quarter based on their prior performance. You can restrict
                which filers are included by using the filters below.
              </v-alert>
            </v-col>

            <!-- Matches found -->
            <v-col cols="12">
              <h4 class="text-subtitle-1">
                Matching filers from latest quarter:
                <v-chip disabled class="text-body-2 text-black">
                  <v-progress-circular
                    v-if="searching"
                    :size="16"
                    :width="2"
                    indeterminate
                    color="primary"
                  />
                  <span v-else>{{ filerPerformanceMatches }}</span>
                </v-chip>
              </h4>
            </v-col>

            <!-- Subscription required alert -->
            <v-col v-if="!accessAllowed({ requires_pro_subscription: true })" cols="12">
              <v-alert :value="true" color="error" :icon="mdiAlert" class="d-inline-flex">
                Pro subscription required to select filers by performance
              </v-alert>
            </v-col>

            <!-- How do you want funds chosen? -->
            <v-col cols="12">
              <helpers-section-sub-header>How do you want funds chosen?</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.top_performing_type"
                :items="topPerformingTypes"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                item-title="description"
                return-object
                style="max-width: 400px"
              />
            </v-col>

            <!-- Maximum Filers to use each quarter -->
            <v-col cols="12">
              <helpers-section-sub-header
                >Maximum Filers to use each quarter</helpers-section-sub-header
              >
              <v-text-field
                v-model="filerPerformance.max_filers"
                :disabled="!accessAllowed({ requires_pro_subscription: true }) || !isEnabled"
                :hint="`Your subscription allows up to ${userLimit} filers`"
                :rules="[
                  value => value <= userLimit || 'Maximum number of filers to use exceed',
                  value => value >= 1 || 'Maximum filers must set to at least 1'
                ]"
                type="number"
                persistent-hint
                style="max-width: 400px"
              />
            </v-col>

            <!-- Insider Ownership of Private Funds -->
            <v-col cols="12">
              <helpers-section-sub-header
                >Insider Ownership of Private Funds</helpers-section-sub-header
              >
              <p>
                Enabling this option sets the earliest allowable start date to 2011. This is the
                earliest date this information was made available.
              </p>
              <v-text-field
                v-model="filerPerformance.adv_insider_ownership"
                :disabled="!accessAllowed({ requires_pro_subscription: true }) || !isEnabled"
                mask="###"
                suffix="%"
                label="Minimum Insider Ownership of Funds"
                style="max-width: 400px"
              />
            </v-col>

            <!-- Quarterly turnover % -->
            <v-col cols="12">
              <helpers-section-sub-header>Quarterly turnover %</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.turnover_type"
                :items="turnoverChoices"
                :disabled="!accessAllowed({ requires_pro_subscription: true }) || !isEnabled"
                item-title="description"
                return-object
                style="max-width: 400px"
              />
            </v-col>

            <!-- % top 10 concentration -->
            <v-col cols="12">
              <helpers-section-sub-header>% top 10 concentration</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.top10_type"
                :items="top10Choices"
                :disabled="!accessAllowed({ requires_pro_subscription: true }) || !isEnabled"
                item-title="description"
                return-object
                style="max-width: 400px"
              />
            </v-col>

            <!-- # of Holdings in 13F Portfolio -->
            <v-col cols="12">
              <helpers-section-sub-header
                ># of Holdings in 13F Portfolio</helpers-section-sub-header
              >
              <v-switch
                v-model="filerPerformance.use_holdings"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Number of Holdings"
                hide-details
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.holdings_from_to[0]"
                    :disabled="!filerPerformance.use_holdings"
                    label="From"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.holdings_from_to[1]"
                    :disabled="!filerPerformance.use_holdings"
                    label="To"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- Market Cap of Fund's 13F Portfolio -->
            <v-col cols="12">
              <helpers-section-sub-header
                >Market Cap of Fund's 13F Portfolio</helpers-section-sub-header
              >
              <v-switch
                v-model="filerPerformance.use_mc"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Market Cap"
                hide-details
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.mc_from_to[0]"
                    :disabled="!filerPerformance.use_mc"
                    label="From"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.mc_from_to[1]"
                    :disabled="!filerPerformance.use_mc"
                    label="To"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- Avg. Time Held Top 10 (quarterly) -->
            <v-col cols="12">
              <helpers-section-sub-header
                >Avg. Time Held Top 10 (quarterly)</helpers-section-sub-header
              >
              <v-switch
                v-model="filerPerformance.use_time_held_top10"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Avg. Time Held Top 10"
                hide-details
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.time_held_top10_from_to[0]"
                    :disabled="!filerPerformance.use_time_held_top10"
                    label="From"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.time_held_top10_from_to[1]"
                    :disabled="!filerPerformance.use_time_held_top10"
                    label="To"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- # of Holdings in 13F Portfolio -->
            <v-col cols="12">
              <helpers-section-sub-header
                >Min/Max number of quarterly 13F Filings</helpers-section-sub-header
              >
              <p>
                Use to limit to funds with a minimum amount of 13F history or use to restrict to new
                funds only.
              </p>
              <v-switch
                v-model="filerPerformance.use_quarters"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Number of Quarterly 13F Filings"
                hide-details
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.quarters_min"
                    :disabled="!filerPerformance.use_quarters"
                    label="Min"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.quarters_max"
                    :disabled="!filerPerformance.use_quarters"
                    label="Max"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// Modules
import api from '@/store/api'
// import { mapFields } from '@/store/helpers'
import debounce from 'lodash/debounce'
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Mixins
import AccessMixin from '@/mixins/access'
import { mdi } from 'vuetify/iconsets/mdi-svg'
import { mdiClose } from '@mdi/js'

export default {
  mixins: [AccessMixin],
  props: {
    whaleTrader: Boolean
  },
  data: () => ({
    mdi,
    mdiClose,
    searching: false,
    filerPerformanceMatches: 0,
    topPerformingTypes: [
      {
        description: 'Do not use dynamic fund selection',
        value: 0,
        requires_subscription: true
      },
      {
        description: 'Top One Year Performance Equal Weight',
        value: 1,
        requires_subscription: true
      },
      {
        description: 'Top Three Year Annualized Equal Weight',
        value: 2,
        requires_subscription: true
      },
      {
        description: 'Top Five Year Annualized Equal Weight',
        value: 3,
        requires_subscription: true
      },
      {
        description: 'Top All-Time Annualized Equal Weight',
        value: 4,
        requires_subscription: true
      },
      {
        description: 'Top One Year Performance Manager Weight',
        value: 5,
        requires_subscription: true
      },
      {
        description: 'Top Three Year Annualized Manager Weight',
        value: 6,
        requires_subscription: true
      },
      {
        description: 'Top Five Year Annualized Manager Weight',
        value: 7,
        requires_subscription: true
      },
      {
        description: 'Top All-Time Annualized Manager Weight',
        value: 8,
        requires_subscription: true
      }
    ],
    turnoverChoices: [
      { value: 0, description: 'Any', requires_pro_subscription: true },
      { value: 1, description: 'No turnover', requires_pro_subscription: true },
      { value: 2, description: '< 10%', requires_pro_subscription: true },
      { value: 3, description: '< 25%', requires_pro_subscription: true },
      { value: 4, description: '< 50%', requires_pro_subscription: true },
      { value: 5, description: '<= 100%', requires_pro_subscription: true },
      { value: 6, description: 'Over 100%', requires_pro_subscription: true }
    ],
    top10Choices: [
      { value: 0, description: 'Any', requires_pro_subscription: true },
      { value: 1, description: '> 10%', requires_subscription: true },
      { value: 2, description: '> 20%', requires_pro_subscription: true },
      { value: 3, description: '> 30%', requires_pro_subscription: true },
      { value: 4, description: '> 40%', requires_pro_subscription: true },
      { value: 5, description: '> 50%', requires_pro_subscription: true },
      { value: 6, description: '> 60%', requires_pro_subscription: true },
      { value: 7, description: '> 70%', requires_pro_subscription: true },
      { value: 8, description: '> 80%', requires_pro_subscription: true }
    ]
  }),

  computed: {
    userLimit() {
      return useUserStore().limit
    },
    backtesterCriteria() {
      return this.whaleTrader
        ? useAppStore().whaleTraderCriteria.signals.f13_signal
        : useAppStore().backtesterCriteria
    },
    filerPerformance() {
      return this.whaleTrader
        ? useAppStore().whaleTraderCriteria.signals.f13_signal.filer_performance
        : useAppStore().backtesterCriteria.filer_performance
    },
    /*  ...mapFields({
      backtesterCriteria: 'app.backtesterCriteria',
      filerPerformance: 'app.backtesterCriteria.filer_performance'
    }), */

    isEnabled() {
      return this.filerPerformance && this.filerPerformance.top_performing_type.value > 0
    }
  },

  watch: {
    filerPerformance: {
      deep: true,
      immediate: true,

      handler(newObj, oldObj) {
        this.searching = true

        if (this.isEnabled) {
          this.filerPerformance.selected = true
          this.backtesterCriteria.whalescore = null
          this.backtesterCriteria.heat_map = false

          // If there's a selected group currently, reset it
          if (useAppStore().selectedGroup.id) {
            useAppStore().setSelectedGroup(null)
          }
        } else {
          this.filerPerformance.selected = false
        }

        this.getMatches()
      }
    }
  },

  methods: {
    // Debounce api calls so we don't query the server on every keystroke
    getMatches: debounce(function () {
      api
        .post('filer_perf_count', this.filerPerformance)
        .then(res => {
          this.filerPerformanceMatches = res.data
        })
        .catch(error => console.log(error))
        .then(() => (this.searching = false))
    }, 500)
  }
}
</script>
